import { useRef, useState } from "react"
import { IconArrowUpFromLineOutline18 as UploadIcon } from "nucleo-ui-outline-18"

import { mutators } from "@productlane/zero/mutators"

import { AvatarCropDialog } from "@/components/avatar-crop-dialog"
import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import {
	LOGO_ACCEPT_STRING,
	LOGO_ACCEPT_TYPES,
	LOGO_MAX_SIZE_BYTES,
	LOGO_MAX_SIZE_MB,
} from "@/screens/settings/portal-instance-form"

interface Props {
	workspaceId: string
	logoUrl: string | null
	onUploaded?: (logoUrl: string) => void | Promise<void>
}

/**
 * The workspace logo, shared by General settings and the portal Theme page so
 * both upload, crop and save it the same way.
 */
export function WorkspaceLogoField({
	workspaceId,
	logoUrl,
	onUploaded,
}: Props) {
	const inputRef = useRef<HTMLInputElement | null>(null)
	const [preview, setPreview] = useState<string | null>(null)
	const [isBroken, setIsBroken] = useState(false)
	const [cropSource, setCropSource] = useState<{
		url: string
		fileName: string
		fileType: string
	} | null>(null)

	const { role } = useRole()
	const zero = useZero()
	const getSignedUrl = api.file.getUrl.useMutation()

	const source = preview ?? logoUrl
	const showImage = !!source && !isBroken

	const upload = async (file: File) => {
		try {
			const { fileUrl, uploadUrl } = await getSignedUrl.mutateAsync({
				fileName: file.name,
				fileType: file.type,
			})
			const res = await fetch(uploadUrl, {
				method: "PUT",
				body: file,
				headers: { "Content-Type": file.type },
			})
			if (!res.ok) throw new Error("File upload failed")

			await mutateOk(
				zero.mutate(
					mutators.workspaces.update({ id: workspaceId, logoUrl: fileUrl }),
				),
			)
			analytics.track("upload-workspace-logo")
			await onUploaded?.(fileUrl)
		} catch (error) {
			toast.error({
				title: "Could not upload the logo",
				description: error instanceof Error ? error.message : undefined,
			})
		}
	}

	const fallback = (
		<div className="flex h-12 w-12 items-center justify-center">
			<UploadIcon className="h-4 w-4 flex-none" />
		</div>
	)

	return (
		<>
			<button
				type="button"
				title="Upload a workspace logo"
				disabled={role === "VIEWER"}
				onClick={() => inputRef.current?.click()}
				className="group relative h-12 w-12 overflow-hidden rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700/80 dark:hover:bg-gray-850"
			>
				{showImage ? (
					<>
						<img
							alt="Workspace logo"
							src={source ?? ""}
							className="h-12 w-12 object-cover"
							onError={() => setIsBroken(true)}
						/>
						<div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100">
							<UploadIcon className="h-4 w-4 flex-none text-white" />
						</div>
					</>
				) : (
					fallback
				)}
			</button>

			<input
				ref={inputRef}
				name="workspace-logo"
				type="file"
				className="hidden"
				accept={LOGO_ACCEPT_STRING}
				onChange={(e) => {
					const file = e.target.files?.[0]
					e.target.value = ""
					if (!file) return
					if (!LOGO_ACCEPT_TYPES.includes(file.type)) {
						toast.info({
							title: "Logos have to be PNG, JPG or WebP",
							description: "SVG does not render in email clients.",
						})
						return
					}
					if (file.size > LOGO_MAX_SIZE_BYTES) {
						toast.info({
							title: `Logos can be at most ${LOGO_MAX_SIZE_MB} MB`,
							description: "Pick a smaller file.",
						})
						return
					}
					setCropSource({
						url: URL.createObjectURL(file),
						fileName: file.name,
						fileType: file.type,
					})
				}}
			/>

			{cropSource ? (
				<AvatarCropDialog
					isOpen
					onOpenChange={(open) => {
						if (!open) {
							URL.revokeObjectURL(cropSource.url)
							setCropSource(null)
						}
					}}
					imageSrc={cropSource.url}
					fileName={cropSource.fileName}
					fileType={cropSource.fileType}
					title="Crop your logo"
					description="Drag to reposition and use the slider to zoom."
					onCropComplete={(croppedFile, previewUrl) => {
						setPreview(previewUrl)
						setIsBroken(false)
						void upload(croppedFile)
					}}
				/>
			) : null}
		</>
	)
}
